import { useEffect, useRef } from "react";
import { isFileDragEvent } from "../fileDrop";

/**
 * Stops a stray OS-file drop anywhere on the page from making the browser
 * navigate away to the file. Guarded to file drags only (capture phase, no
 * stopPropagation), so the app's internal HTML5 drags and dedicated dropzones
 * keep working — their own handlers still fire.
 *
 * `onFileDragEnd` fires when a file drag ends anywhere — dropped (on a target
 * or not) or dragged out of the window. OS file drags have no in-page drag
 * source, so `dragend` never fires for them; pages tracking hover state with
 * a depth counter need this to avoid a stranded highlight after a cancelled
 * or missed drop.
 */
export function usePreventWindowFileDrop(onFileDragEnd?: () => void): void {
  const onEndRef = useRef(onFileDragEnd);
  onEndRef.current = onFileDragEnd;

  useEffect(() => {
    const onDragOver = (e: DragEvent) => {
      if (isFileDragEvent(e)) {
        e.preventDefault();
      }
    };
    const onDrop = (e: DragEvent) => {
      if (isFileDragEvent(e)) {
        e.preventDefault();
        onEndRef.current?.();
      }
    };
    const onDragLeave = (e: DragEvent) => {
      // relatedTarget is null when the drag leaves the window entirely
      if (e.relatedTarget === null && isFileDragEvent(e)) {
        onEndRef.current?.();
      }
    };
    window.addEventListener("dragover", onDragOver, { capture: true });
    window.addEventListener("drop", onDrop, { capture: true });
    window.addEventListener("dragleave", onDragLeave, { capture: true });
    return () => {
      window.removeEventListener("dragover", onDragOver, { capture: true });
      window.removeEventListener("drop", onDrop, { capture: true });
      window.removeEventListener("dragleave", onDragLeave, { capture: true });
    };
  }, []);
}
